export interface PDFDocument {
  id: string
  name: string
  url: string
  status: 'draft' | 'reviewed' | 'approved'
  pageCount: number
  loadedAt: string
  source?: 'backend' | 'upload'
  kind?: string | null
  slotLabel?: string | null
  localPath?: string
}

export interface NormalizedPoint {
  x: number
  y: number
}

export interface NormalizedRect {
  x: number
  y: number
  width: number
  height: number
}

export type ShapeType =
  | 'rectangle'
  | 'ellipse'
  | 'polygon'
  | 'polyline'
  | 'arc'
  | 'line'
  | 'arrow'
  | 'cloud'
  | 'cloud-polygon'
  | 'freehand'
  | 'highlighter'
  | 'note'
  | 'hyperlink'
  | 'text'
  | 'text-box'
  | 'callout'
  | 'typewriter'
  | 'stamp'
  | 'measurement'
  | 'measurement-line'
  | 'measurement-polygon'
  | 'measurement-circle'
  | 'triangle'
  | 'rhombus'
  | 'dimension-length'
  | 'dimension-continuous'
  | 'dimension-baseline'
  | 'dimension-area'
  | 'dimension-perimeter'
  | 'dimension-radius'
  | 'dimension-diameter'
  | 'dimension-angle'

export interface ShapeStyle {
  fill: string
  stroke: string
  strokeWidth: number
  opacity: number
  fillOpacity?: number
  strokeOpacity?: number
  strokeDasharray?: number[]
}

export type FontFamily =
  | 'Helvetica'
  | 'Times'
  | 'Courier'
  | 'Roboto'
  | 'OpenSans'
  | 'Lato'
  | 'Montserrat'
  | 'Poppins'
  | 'Merriweather'
  | 'SourceCodePro'
export type FontStyle = 'normal' | 'bold' | 'italic' | 'bold-italic'
export type TextAlign = 'left' | 'center' | 'right' | 'justify'
export type VerticalAlign = 'top' | 'middle' | 'bottom'
export type StrokeStyle = 'solid' | 'dashed' | 'dotted'
export type LineStyle =
  | 'solid'
  | 'dashed-1'
  | 'dashed-2'
  | 'dashed-3'
  | 'dashed-4'
  | 'dashed-5'
  | 'dashed-6'
  | 'cloud'
export type CalloutBoxShape = 'rectangle' | 'ellipse' | 'triangle' | 'hexagon'
export type ArrowheadStyle =
  | 'none'
  | 'open'
  | 'closed'
  | 'dot'
  | 'diamond'
  | 'slash'
  | 'bar'
  | 'reverse'

export interface TextMarkupProps {
  content: string
  fontFamily: FontFamily
  fontSize: number
  fontStyle: FontStyle
  underline: boolean
  strikethrough: boolean
  textColor: string
  textAlign: TextAlign
  verticalAlign: VerticalAlign
  lineHeight: number
  margin: number
  autoSize: boolean
}

export type StampTemplate =
  | 'date'
  | 'seal'
  | 'approved'
  | 'reviewed'
  | 'rejected'
  | 'draft'
  | 'confidential'
  | 'received'
  | 'for-review'
  | 'completed'
  | 'void'
  | 'preliminary'
  | 'released'
  | 'revised'
  | 'proprietary'

export type StampLayout = 'badge' | 'watermark' | 'tag' | 'seal'

export type StampDateFormat = 'us' | 'european'

export interface BaseShape {
  id: string
  documentId: string
  pageIndex: number
  type: ShapeType
  style: ShapeStyle
  rotation?: number
  layerId?: string
  isUserMarkup: boolean
  createdAt: string
  name?: string
  visible?: boolean
  locked?: boolean
  sourceKind?: string
  sourceRef?: string
}

export interface RectangleShape extends BaseShape {
  type: 'rectangle' | 'highlighter'
  coords: NormalizedRect
  isHighlight?: boolean
}

export interface EllipseShape extends BaseShape {
  type: 'ellipse'
  coords: NormalizedRect
}

export type TriangleOrientation = 'up' | 'down'

export interface TriangleShape extends BaseShape {
  type: 'triangle'
  coords: NormalizedRect
  orientation?: TriangleOrientation
}

export interface RhombusShape extends BaseShape {
  type: 'rhombus'
  coords: NormalizedRect
}

export interface CloudShape extends BaseShape {
  type: 'cloud'
  coords: NormalizedRect
}

export interface CloudPolygonShape extends BaseShape {
  type: 'cloud-polygon'
  points: NormalizedPoint[]
}

export type HyperlinkActionKind =
  | 'jump'
  | 'page'
  | 'place'
  | 'space'
  | 'snapshot'
  | 'url'
  | 'open'

export type HyperlinkScale = 'fit-page' | 'fit-width' | 'actual'

export interface HyperlinkAction {
  kind: HyperlinkActionKind
  documentId?: string
  jumpPageIndex?: number
  pageIndex?: number
  scale?: HyperlinkScale
  placeId?: string
  snapshotPageIndex?: number
  snapshotRect?: NormalizedRect
  url?: string
  openInNewTab?: boolean
  relativePath?: boolean
}

export interface HyperlinkShape extends BaseShape {
  type: 'hyperlink'
  coords: NormalizedRect
  action: HyperlinkAction
}

export interface PolygonShape extends BaseShape {
  type: 'polygon'
  points: NormalizedPoint[]
}

export interface PolylineShape extends BaseShape {
  type: 'polyline'
  points: NormalizedPoint[]
}

export interface FreehandShape extends BaseShape {
  type: 'freehand'
  points: NormalizedPoint[]
  isHighlight?: boolean
}

export interface ArcShape extends BaseShape {
  type: 'arc'
  start: NormalizedPoint
  end: NormalizedPoint
  control: NormalizedPoint
}

export interface LineShape extends BaseShape {
  type: 'line' | 'arrow'
  start: NormalizedPoint
  end: NormalizedPoint
}

export type NoteIconType =
  | 'note'
  | 'check'
  | 'circle'
  | 'comment'
  | 'cross'
  | 'question'
  | 'warning'
  | 'star'
  | 'arrow-right'
  | 'paragraph'
  | 'flag'

export interface NoteShape extends BaseShape {
  type: 'note'
  position: NormalizedPoint
  text: string
  color: string
  author?: string
  date?: string
  open?: boolean
  iconType?: NoteIconType
  boxOffset?: NormalizedPoint
  boxHidden?: boolean
}

export interface TextShape extends BaseShape {
  type: 'text'
  position: NormalizedPoint
  content: string
  fontSize: number
}

export interface TextBoxShape extends BaseShape, TextMarkupProps {
  type: 'text-box'
  coords: NormalizedRect
  cornerRadius?: number
  padding?: number
  boxShape?: CalloutBoxShape
  lineStyle?: LineStyle
}

export interface CalloutShape extends BaseShape, TextMarkupProps {
  type: 'callout'
  coords: NormalizedRect
  target: NormalizedPoint
  knee?: NormalizedPoint
  arrowhead: ArrowheadStyle
  cornerRadius?: number
  padding?: number
  boxShape?: CalloutBoxShape
  lineStyle?: LineStyle
}

export interface TypewriterShape extends BaseShape, TextMarkupProps {
  type: 'typewriter'
  coords: NormalizedRect
}

export interface StampShape extends BaseShape {
  type: 'stamp'
  coords: NormalizedRect
  template: StampTemplate
  label: string
  color: string
  layout: StampLayout
  showDate: boolean
  showTime: boolean
  dateFormat: StampDateFormat
  showAuthor: boolean
  author?: string
  date?: string
  fontFamily: FontFamily
  fontSize: number
  fontStyle: FontStyle
  textColor: string
  textAlign: TextAlign
}

export interface MeasurementShape extends BaseShape {
  type: 'measurement'
  start: NormalizedPoint
  end: NormalizedPoint
  distance?: number
  unit?: string
}

export interface MeasurementLineShape extends BaseShape {
  type: 'measurement-line'
  start: NormalizedPoint
  end: NormalizedPoint
  unit?: string
  showLabel?: boolean
}

export interface MeasurementPolygonShape extends BaseShape {
  type: 'measurement-polygon'
  points: NormalizedPoint[]
  unit?: string
  showLabel?: boolean
}

export interface MeasurementCircleShape extends BaseShape {
  type: 'measurement-circle'
  center: NormalizedPoint
  radius: number
  unit?: string
  showLabel?: boolean
}

export type UnitSystem = 'imperial' | 'metric' | 'architectural' | 'decimal'

export type DimensionAlignment =
  | 'aligned'
  | 'horizontal'
  | 'vertical'
  | 'rotated'

export type DimensionStatus = 'none' | 'reviewed' | 'approved' | 'rejected'

export interface DimensionAppearance {
  extensionLineLength: number
  extensionLineOffset: number
  startArrowStyle: ArrowheadStyle
  endArrowStyle: ArrowheadStyle
  arrowSize: number
  showExtensionLines: boolean
  showDimensionLine: boolean
  showLeader: boolean
  textPosition: NormalizedPoint | null
  textRotation: number
  textAlign: TextAlign
  font: FontFamily
  fontSize: number
  textColor: string
  prefix?: string
  suffix?: string
  customLabel?: string
  precision: number
  rounding?: number
}

export interface DimensionMeta {
  subject?: string
  label?: string
  author?: string
  dateModified?: string
  status?: DimensionStatus
  locked?: boolean
  unit: UnitSystem
}

export interface DimensionLengthShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-length'
  start: NormalizedPoint
  end: NormalizedPoint
  alignment: DimensionAlignment
}

export interface DimensionContinuousShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-continuous'
  points: NormalizedPoint[]
  alignment: DimensionAlignment
  showTotal: boolean
}

export interface DimensionBaselineShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-baseline'
  origin: NormalizedPoint
  targets: NormalizedPoint[]
  spacing: number
  alignment: DimensionAlignment
}

export interface DimensionAreaShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-area'
  points: NormalizedPoint[]
  showFill: boolean
}

export interface DimensionPerimeterShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-perimeter'
  points: NormalizedPoint[]
  closed: boolean
}

export interface DimensionRadialShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-radius' | 'dimension-diameter'
  center: NormalizedPoint
  radiusPoint: NormalizedPoint
  leaderEnd: NormalizedPoint | null
  symbol: 'R' | '⌀'
}

export interface DimensionAngleShape
  extends BaseShape,
    DimensionAppearance,
    DimensionMeta {
  type: 'dimension-angle'
  vertex: NormalizedPoint
  arm1: NormalizedPoint
  arm2: NormalizedPoint
  arcRadius: number
}

export type DimensionShape =
  | DimensionLengthShape
  | DimensionContinuousShape
  | DimensionBaselineShape
  | DimensionAreaShape
  | DimensionPerimeterShape
  | DimensionRadialShape
  | DimensionAngleShape

export function isDimensionShape(shape: Shape): shape is DimensionShape {
  return shape.type.startsWith('dimension-')
}

export interface PageScaleConfig {
  scaleFactor: number
  scaleFactorY?: number
  unit: UnitSystem
  precision: number
}

export interface ViewportBinding {
  id: string
  name: string
  scaleConfigId: string
}

export type PlaceLocation =
  | { kind: 'page'; pageIndex: number; scale: HyperlinkScale }
  | {
      kind: 'current-view'
      pageIndex: number
      zoom: number
      panX: number
      panY: number
    }
  | { kind: 'snapshot'; pageIndex: number; rect: NormalizedRect }

export interface Place {
  id: string
  name: string
  location: PlaceLocation
}

export interface SavedLayer {
  id: string
  name: string
  color: string
  visible: boolean
  origin: 'ai' | 'user'
  shapes: Shape[]
}

export type SavedMeasurementShape =
  | MeasurementLineShape
  | MeasurementPolygonShape
  | MeasurementCircleShape

export interface MeasurementScaleConfig {
  scaleFactor: number
  scaleFactorY?: number
  unit: 'inches'
  precision?: number
}

export interface PlaygroundSaveState {
  version: 1
  documentId: string
  savedAt: string
  layers: SavedLayer[]
  measurements: SavedMeasurementShape[]
  scaleConfig: MeasurementScaleConfig | null
  pageScaleOverrides: Record<number, MeasurementScaleConfig>
  viewports: ViewportBinding[]
  places: Place[]
}

export type Shape =
  | RectangleShape
  | EllipseShape
  | TriangleShape
  | RhombusShape
  | CloudShape
  | CloudPolygonShape
  | HyperlinkShape
  | PolygonShape
  | PolylineShape
  | ArcShape
  | FreehandShape
  | LineShape
  | NoteShape
  | TextShape
  | TextBoxShape
  | CalloutShape
  | TypewriterShape
  | StampShape
  | MeasurementShape
  | MeasurementLineShape
  | MeasurementPolygonShape
  | MeasurementCircleShape
  | DimensionLengthShape
  | DimensionContinuousShape
  | DimensionBaselineShape
  | DimensionAreaShape
  | DimensionPerimeterShape
  | DimensionRadialShape
  | DimensionAngleShape

export type OverlayGeometry =
  | { type: 'rectangle'; coords: NormalizedRect }
  | { type: 'ellipse'; coords: NormalizedRect }
  | { type: 'triangle'; coords: NormalizedRect }
  | { type: 'rhombus'; coords: NormalizedRect }
  | { type: 'cloud'; coords: NormalizedRect }
  | { type: 'polygon'; points: NormalizedPoint[] }
  | { type: 'freehand'; points: NormalizedPoint[] }
  | { type: 'line'; start: NormalizedPoint; end: NormalizedPoint }
  | { type: 'point'; position: NormalizedPoint }

export interface OverlayItem {
  id: string
  pageIndex: number
  type: ShapeType
  geometry: OverlayGeometry
  style: ShapeStyle
  layerId?: string
}

export interface Layer {
  id: string
  documentId: string
  name: string
  color: string
  visible: boolean
  shapes: Shape[]
  origin?: 'ai' | 'user'
}

export interface CommentAuthor {
  name: string
  avatar?: string
}

export enum CommentType {
  AIComment = 1,
  CityClerkComment = 2,
  QpointCoreComment = 3,
}

export interface Replies {
  replies: CommentReply[]
}
export interface CommentReply {
  id: string
  text: string
  createdDate: string
  creatorFullName?: string
  commentType?: CommentType
}
export interface CommentReplyRequest {
  commentId: string
  text: string
  attachmentId: string
  commentType: CommentType
}

export type CommentStatus = 'open' | 'in-progress' | 'resolved'

export interface Comment {
  id: string
  documentId: string
  pageIndex: number
  xNorm: number
  yNorm: number
  status: CommentStatus
  creaetorFullName?: string
  text: string
  createdAt: string
  replyCount: number
  box?: NormalizedRect
  boxConnectorOffset?: number
  pinAngle?: number
  replies: CommentReply[]
  isAiComment?: boolean
  isImplicitAIComment?: boolean
  commentType?: CommentType
  replyTargetId?: string
  position?: NormalizedPoint
  content?: string
  resolved?: boolean
  markerType?: 'round' | 'arrow'
  sitePlanEntityId?: string | number
  accentColor?: string
  accentPillBg?: string
  accentPillText?: string
}

export interface ViewerState {
  zoom: number
  panX: number
  panY: number
  currentPage: number
  fitZoom?: number
  fitPanX?: number
  fitPanY?: number
}

export type ToolType =
  | 'select'
  | 'hand'
  | 'rectangle'
  | 'ellipse'
  | 'polygon'
  | 'polyline'
  | 'arc'
  | 'line'
  | 'arrow'
  | 'cloud'
  | 'cloud-polygon'
  | 'freehand'
  | 'highlighter'
  | 'note'
  | 'flag'
  | 'hyperlink'
  | 'text'
  | 'text-box'
  | 'callout'
  | 'typewriter'
  | 'stamp'
  | 'eraser'
  | 'ruler'
  | 'area'
  | 'measure-circle'
  | 'calibrate'
  | 'comment'
  | 'triangle'
  | 'rhombus'
  | 'dimension-length'
  | 'dimension-continuous'
  | 'dimension-baseline'
  | 'dimension-area'
  | 'dimension-perimeter'
  | 'dimension-radius'
  | 'dimension-diameter'
  | 'dimension-angle'
  | 'snapshot-capture'

export interface ScaleCalibration {
  documentId: string
  pixelDistance: number
  realWorldDistance: number
  unit: 'meters' | 'cm' | 'inches'
}

export interface DocumentScaleConfig {
  documentId: string
  scaleFactor: number
  unit: 'meters' | 'cm' | 'inches'
}

export type MeasurementSubTool = 'distance' | 'perimeter' | 'circle'

export interface MeasurementExportData {
  version: 1
  documentId: string
  scale?: { scaleFactor: number; unit: string }
  lines: Array<{
    id: string
    pageIndex: number
    start: NormalizedPoint
    end: NormalizedPoint
    unit?: string
  }>
  polygons: Array<{
    id: string
    pageIndex: number
    points: NormalizedPoint[]
    unit?: string
  }>
  circles: Array<{
    id: string
    pageIndex: number
    center: NormalizedPoint
    radius: number
    unit?: string
  }>
}

export interface OverlaysResponse {
  documentId: string
  layers: Layer[]
}

export interface CommentsResponse {
  documentId: string
  comments: Comment[]
}
